/**
 * @file redirect-probe.ts
 * @description Ответ витрины словами для окна «Проверить» (phase19.16,
 * партия Р3в, v0.19.64; SB132; контракт r3v-api-contract §1 probe):
 * «301 — переадресация → /about-us», «200 — открывается страница», «404 —
 * страница не найдена»; совпал ли полученный ответ с ожидаемым. Коды и
 * причины сбоя — реестрами.
 */

import type { SiteRedirectProbeResult } from "../api-site-redirects";

const STATUS_TEXT: Readonly<Record<number, string>> = {
  200: "открывается страница",
  301: "переадресация",
  302: "временная переадресация",
  307: "временная переадресация",
  308: "переадресация",
  404: "страница не найдена",
  410: "страница удалена",
  503: "сайт закрыт на работы",
};

const OBSERVE_ERROR_TEXT: Readonly<Record<string, string>> = {
  web_not_configured: "адрес сайта для проверки не настроен",
  timeout: "сайт не ответил за 3 секунды",
  web_unavailable: "сайт недоступен",
  too_many_hops: "слишком много переадресаций подряд",
};

/** «301 — переадресация → /about-us». */
export function probeLine(status: number | null, location: string | null): string {
  if (status === null) return "служебный адрес — переадресации не касаются";
  const text = STATUS_TEXT[status];
  const head = text ? `${status} — ${text}` : String(status);
  return location ? `${head} → ${location}` : head;
}

/** Путь из полного адреса прыжка (адреса витрины — относительные). */
function pathOf(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, "http://x");
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

/** Полученный ответ словами: первый прыжок (что увидит браузер) или причина сбоя. */
export function observedLine(result: SiteRedirectProbeResult): string {
  const { observed } = result;
  if (!observed.ok) return `не удалось спросить сайт: ${OBSERVE_ERROR_TEXT[observed.error] ?? observed.error}`;
  const first = observed.hops[0];
  if (!first) return probeLine(observed.finalStatus, null);
  return probeLine(first.status, pathOf(first.location));
}

/** Совпал ли ответ; не удалось спросить витрину или ожидание не считается — null. */
export function probeMatches(result: SiteRedirectProbeResult): boolean | null {
  const { observed, expected } = result;
  if (!observed.ok || expected.status === null) return null;
  const first = observed.hops[0];
  const status = first?.status ?? observed.finalStatus;
  const location = pathOf(first?.location ?? null);
  return status === expected.status && (expected.location === null || location === expected.location);
}
